import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MessageCircle, ShoppingBag } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';

const Checkout = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', address: '', phone: '', notes: '' });

  const { data: whatsappNumber } = useQuery({
    queryKey: ['whatsapp-number'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('value').eq('key', 'whatsapp_number').single();
      return data?.value || '';
    },
  });

  const createOrder = useMutation({
    mutationFn: async () => {
      for (const item of items) {
        const { error } = await supabase.from('orders').insert({
          user_id: user!.id,
          artwork_id: item.id,
          customer_name: form.name,
          address: form.address,
          phone: form.phone,
          notes: form.notes,
          total_price: item.price,
          status: 'pending',
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success(t('orderSuccess'));

      const itemsList = items.map(item => {
        const title = lang === 'ar' && item.title_ar ? item.title_ar : item.title;
        return `• ${title} - ${t('currency')}${item.price}`;
      }).join('\n');

      const message = `🎨 *Melody Art Gallery - New Order*\n\n` +
        `📦 Items:\n${itemsList}\n\n` +
        `💰 Total: ${t('currency')}${total}\n` +
        `👤 Name: ${form.name}\n` +
        `📍 Address: ${form.address}\n` +
        `📞 Phone: ${form.phone}\n` +
        `📝 Notes: ${form.notes || 'N/A'}`;

      const number = whatsappNumber?.replace(/[^0-9]/g, '') || '';
      if (number) {
        window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, '_blank');
      }

      clearCart();
      navigate('/orders');
    },
    onError: () => toast.error(t('error')),
  });

  if (!user) return <Navigate to="/auth" replace />;
  if (items.length === 0) return <Navigate to="/cart" replace />;

  return (
    <div className="min-h-screen pt-20">
      <div className="container mx-auto px-4 py-12 max-w-lg">
        <h1 className="font-display text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
          <ShoppingBag className="h-6 w-6 text-primary" />
          {t('orderTitle')}
        </h1>

        {/* Order summary */}
        <div className="glass-panel border-0 rounded-xl p-4 mb-6">
          {items.map(item => (
            <div key={item.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
              <span className="text-sm text-foreground truncate flex-1">
                {lang === 'ar' && item.title_ar ? item.title_ar : item.title}
              </span>
              <span className="text-sm font-semibold text-primary">{t('currency')}{item.price}</span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 mt-2 border-t border-border/50">
            <span className="font-medium text-foreground">{t('totalPrice')}</span>
            <span className="text-lg font-bold text-primary">{t('currency')}{total}</span>
          </div>
        </div>

        <form onSubmit={e => { e.preventDefault(); createOrder.mutate(); }} className="space-y-4 glass-panel border-0 p-6 rounded-2xl">
          <div>
            <Label className="text-foreground">{t('fullName')}</Label>
            <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="glass-input" />
          </div>
          <div>
            <Label className="text-foreground">{t('address')}</Label>
            <Input required value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="glass-input" />
          </div>
          <div>
            <Label className="text-foreground">{t('phone')}</Label>
            <Input required type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="glass-input" />
          </div>
          <div>
            <Label className="text-foreground">{t('notes')}</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="glass-input" rows={3} />
          </div>
          <Button type="submit" disabled={createOrder.isPending} className="w-full gradient-accent text-primary-foreground shadow-glow" size="lg">
            <MessageCircle className={`h-4 w-4 ${lang === 'ar' ? 'ml-2' : 'mr-2'}`} />
            {t('submitOrder')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
